import React, { useState } from "react"
import { Check, Eye, File, Info } from "lucide-react"
import ModalForm from "components/common/modal/ModalForm"
import { RechargeRequest } from "src/types/RechargementRequest"
import { formatMoney, getFilePath } from "@/utils/utils"
import DebitClientModal from "components/clientDataTable/DebitClientForm"

type Props = {
  request: RechargeRequest
  onValidate?: (id: string) => void
  onReject?: (id: string, comment: string) => void
}


const RechargeProofPreview = ({ request }: { request: RechargeRequest }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 items-start gap-6">
        <div>
          <span className="inline-block text-xs px-3 py-1 rounded-md bg-purple-100 text-purple-700">
            {request.method}
          </span>
        </div>

        <div>
          <p className="text-xs text-gray-500">Client</p>
          <p className="font-medium text-gray-900">{request.client}</p>
        </div>

        <div>
          <p className="text-xs text-gray-500">Montant</p>
          <p className="font-semibold text-gray-900">
            {formatMoney(request.amount)} Fcfa
          </p>
        </div>
      </div>

      <div className="flex justify-center ">
        <img src={getFilePath("preuve.png")} alt="preuve" />

      </div>

      <div>
        <button className="border px-4 py-2 rounded-md text-sm hover:bg-gray-50">
          Télécharger le justificatif
        </button>
      </div>

      <div>
        <p className="text-xs text-gray-500 mb-1">Note</p>
        <p className="text-sm text-gray-700">
          {request.description || "Aucune note fournie"}
        </p>
      </div>
    </div>
  )
}


const RechargeProofContent = ({ request }: { request: RechargeRequest }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 items-start gap-6">
        <div>
          <span className="inline-block text-xs px-3 py-1 rounded-md bg-purple-100 text-purple-700">
            {request.method}
          </span>
        </div>

        <div>
          <p className="text-xs text-gray-500">Client</p>
          <p className="font-medium text-gray-900">{request.client}</p>
        </div>

        <div>
          <p className="text-xs text-gray-500">Montant</p>
          <p className="font-semibold text-gray-900">
            {formatMoney(request.amount)} Fcfa
          </p>
        </div>
      </div>

      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="bg-red-500 text-white p-2 border rounded-lg">
            <File size={18} />
          </span>
          <p className="text-sm font-medium">
            Fichier justificatif virement
          </p>
        </div>

        <button className="border px-3 py-1 text-sm rounded hover:bg-gray-50">
          Télécharger le justificatif
        </button>
      </div>

      <div>
        <p className="text-xs text-gray-500 mb-1">Note</p>
        <p className="text-sm text-gray-700">
          {request.description || "Aucune note fournie"}
        </p>
      </div>
    </div>
  )
}


const RechargeRejectContent = ({
                                 request,
                                 comment,
                                 setComment,
                               }: {
  request: RechargeRequest
  comment: string
  setComment: (value: string) => void
}) => {
  return (
    <div className="space-y-6">
      <RechargeProofContent request={request} />

      <div>
        <p className="text-xs text-gray-500 mb-2">
          Commentaire de rejet
        </p>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Saisir le commentaire de rejet..."
          className="w-full min-h-[120px] border rounded-lg p-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-red-400"
        />
      </div>
    </div>
  )
}


const RechargeRequestItem = ({
                               request,
                               onValidate,
                               onReject,
                             }: Props) => {
  const [openPreview, setOpenPreview] = useState(false)
  const [openValidateModal, setOpenValidateModal] = useState(false)
  const [openRejectModal, setOpenRejectModal] = useState(false)
  const [rejectComment, setRejectComment] = useState("")

  return (
    <>
      <div className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm">
        <div className="flex items-start gap-4">
          {/*<img*/}
          {/*  src="/preuve.png"*/}
          {/*  alt="preuve"*/}
          {/*  className="w-[96px] h-[96px] rounded-lg"*/}
          {/*/>*/}
          <img src={getFilePath("preuve.png")} alt="preuve" className="w-[96px] h-[96px] rounded-lg"/>

          <div className="space-y-1">
            <p className="text-xs text-gray-500">
              Date : {request.date}
            </p>

            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-gray-900">
                {request.client}
              </p>

              {request.reference && (
                <span className="text-xs text-gray-400">
                  • {request.reference} •
                </span>
              )}

              <span className="text-xs px-2 py-1 rounded-md bg-purple-100 text-purple-700 border border-[#C084FC] font-medium">
                {request.method}
              </span>
            </div>

            <p className="text-sm text-gray-700">
              <span className="text-gray-500">Montant :</span>{" "}
              <span className="font-semibold">
                {formatMoney(request.amount)} Fcfa
              </span>
            </p>

            <p className="text-xs text-gray-400">
              {request.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpenPreview(true)}
            className="p-2 rounded-md hover:bg-gray-50"
          >
            <Eye size={18} />
          </button>

          {request.status === "PENDING" && (
            <>
              <button
                onClick={() => setOpenValidateModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-validate text-white flex items-center gap-2"
              >
                <Check size={16} />
                Valider
              </button>

              <button
                onClick={() => setOpenRejectModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-white text-red-600 border border-red-500 hover:bg-red-50 flex items-center gap-2"
              >
                ✕ Rejeter
              </button>
            </>
          )}
        </div>
      </div>



      <ModalForm
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        title={`Preuve de paiement - ${request.reference ?? "RCH-002"}`}
        // position="center"
      >
        <RechargeProofPreview request={request} />
      </ModalForm>

      <ModalForm
        open={openValidateModal}
        onClose={() => setOpenValidateModal(false)}
        title="Preuve de paiement"
        // position="center"
        submitButtonLabel={
          <span className="flex items-center gap-2">
            <Check size={16} />
            Valider
          </span>
        }
        cancelButtonLabel="Annuler"
        submitButtonProps={{
          className:
            "bg-green-700 hover:bg-green-800 text-white border-none",
        }}
        formSubmit={() => {
          onValidate?.(request.id)
          setOpenValidateModal(false)
        }}
      >
        <RechargeProofContent request={request} />
      </ModalForm>

      <ModalForm
        open={openRejectModal}
        onClose={() => setOpenRejectModal(false)}
        title="Rejeter la demande de rechargement"
        // position="center"
        submitButtonLabel={
          <span className="flex items-center gap-2">
            ✕ Confirmer le rejet
          </span>
        }
        cancelButtonLabel="Annuler"
        submitButtonProps={{
          className:
            "bg-red-600 hover:bg-red-700 text-white border-none",
        }}
        formSubmit={() => {
          onReject?.(request.id, rejectComment)
          setRejectComment("")
          setOpenRejectModal(false)
        }}
      >
        <RechargeRejectContent
          request={request}
          comment={rejectComment}
          setComment={setRejectComment}
        />
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Info className="w-4 h-4"/>
           Ce commentaire sera visible par le client
        </p>
      </ModalForm>
    </>
  )
}

export default RechargeRequestItem
